import React from 'react';
import styled from 'styled-components';
import {
  LogOut,
  ChevronLeft,
  ChevronRight,
  User,
} from 'lucide-react';
import AuthService from '../../services/AuthService';
import { getCookie } from '../../services/DataRequestService';
import demandEdgeLogo from '../../assets/brand/demand-edge-logo-preview.png';
import demandEdgeIcon from '../../assets/brand/demand-edge-icon.png';

export default function AdminNavbar({ collapsed = false, onToggle }) {
  const username = getCookie('username')?.toUpperCase() || 'USER';

  const doLogout = () => {
    AuthService.logout();
  };

  return (
    <Sidebar $collapsed={collapsed}>
      <div className="de-sidebar-inner">
        <div className="de-sidebar-brand">
          <a href="/" aria-label="Demand Edge" className="de-sidebar-brand-link">
            {collapsed ? (
              <span className="de-sidebar-logo-card de-sidebar-logo-card--icon">
                <img
                  src={demandEdgeIcon}
                  className="de-sidebar-logo-icon"
                  alt="Demand Edge"
                />
              </span>
            ) : (
              <span className="de-sidebar-logo-card">
                <img
                  src={demandEdgeLogo}
                  className="de-sidebar-logo"
                  alt="Demand Edge"
                />
              </span>
            )}
          </a>
          <button
            type="button"
            className="de-sidebar-toggle"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        <div className="de-sidebar-user">
          <div className="de-sidebar-user-icon">
            <User size={18} strokeWidth={2} />
          </div>
          {!collapsed && (
            <div className="de-sidebar-user-text">
              <span>Welcome to Demand Edge</span>
              <strong>{username}</strong>
            </div>
          )}
        </div>

        <button
          type="button"
          className="de-sidebar-logout"
          onClick={doLogout}
          title="Logout"
        >
          <LogOut size={16} strokeWidth={2} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </Sidebar>
  );
}

const Sidebar = styled.aside`
  --de-navy: #032b4e;
  --de-navy-dark: #021f3a;
  --de-red: #ac1424;
  --de-sidebar-width: 272px;
  --de-sidebar-collapsed: 88px;

  position: fixed;
  top: 0;
  left: 0;
  z-index: 200;
  width: ${({ $collapsed }) => ($collapsed ? 'var(--de-sidebar-collapsed)' : 'var(--de-sidebar-width)')};
  height: 100vh;
  background: linear-gradient(180deg, var(--de-navy) 0%, var(--de-navy-dark) 100%);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 4px 0 24px rgba(3, 43, 78, 0.18);
  transition: width 0.25s ease;
  overflow: hidden;

  ${({ $collapsed }) =>
    $collapsed &&
    `
    .de-sidebar-inner {
      padding: 14px 12px 20px;
      align-items: center;
    }

    .de-sidebar-brand {
      flex-direction: column;
      align-items: center;
      gap: 10px;
      width: 100%;
      margin-bottom: 14px;
      padding-bottom: 12px;
    }

    .de-sidebar-brand-link {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .de-sidebar-toggle {
      width: 42px;
      height: 42px;
    }

    .de-sidebar-user {
      width: 46px;
      height: 46px;
      padding: 0;
      margin: 0 0 10px;
      background: transparent;
      border: none;
      justify-content: center;
    }

    .de-sidebar-user-icon {
      width: 46px;
      height: 46px;
      min-width: 46px;
    }

    .de-sidebar-logout {
      width: 46px;
      height: 46px;
      padding: 0;
      margin: 0;
    }
  `}

  .de-sidebar-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 20px 14px 24px;
  }

  .de-sidebar-brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 24px;
    padding-bottom: 18px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .de-sidebar-brand-link {
    display: flex;
    align-items: center;
    min-width: 0;
    flex: 1;
    text-decoration: none;
  }

  .de-sidebar-logo-card {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 210px;
    height: fit-content;
    padding: 6px 10px;
    background: #f7f8fa;
    border: 1px solid rgba(255, 255, 255, 0.45);
    border-radius: 8px;
    box-sizing: border-box;
  }

  .de-sidebar-logo {
    display: block;
    width: 100%;
    height: auto;
    max-height: 48px;
    object-fit: contain;
  }

  .de-sidebar-logo-card--icon {
    width: 44px;
    height: 44px;
    max-width: 44px;
    padding: 4px;
  }

  .de-sidebar-logo-icon {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .de-sidebar-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    cursor: pointer;
    flex-shrink: 0;
  }

  .de-sidebar-user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    margin-bottom: 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
  }

  .de-sidebar-user-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    min-width: 40px;
    border-radius: 10px;
    background: #1b85ba;
    color: #fff;
  }

  .de-sidebar-user-text span {
    display: block;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.65);
  }

  .de-sidebar-user-text strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
  }

  .de-sidebar-logout {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    width: 100%;
    padding: 10px 14px;
    border: none;
    border-radius: 10px;
    background: #c9552a;
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }

  .de-sidebar-logout:hover {
    background: #a84420;
    transform: translateY(-1px);
  }
`;
